import { SharedNavbar } from "@/components/SharedNavbar";
import { GlobalToolsExpandingPanel } from "@/components/GlobalToolsExpandingPanel";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Layers } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { usePermission } from "@/hooks/usePermissions";
import { QuickActions } from "@/components/QuickActions";
import { supabase } from "@/lib/supabase";
import { motion } from "framer-motion";
import { DemoBanner } from "@/components/DemoBanner";
import { DemoExpiredModal } from "@/components/DemoExpiredModal";

function PageHeader4({ breadcrumbs = [], pageTitle = "", pageDescription = "" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { userProfile, company } = useAuth();

  const [stackInfo, setStackInfo] = useState<{ stackId: string; stackName: string } | null>(null);

  // Check if current project is part of a stack
  useEffect(() => {
    const checkIfInStack = async () => {
      // Get projectId from URL params
      const projectId = params.projectId;

      if (!projectId) {
        return;
      }

      // First check location state (from navigation)
      if (location.state?.fromStack) {
        const info = {
          stackId: location.state.fromStack,
          stackName: location.state.stackName || 'Stack'
        };
        console.log('[AppShell3] Found stack info in location.state:', info);
        setStackInfo(info);

        // Store in sessionStorage so it persists across reloads
        sessionStorage.setItem(`stack_context_${projectId}`, JSON.stringify(info));
        return;
      }

      // Check sessionStorage for persisted context (do this BEFORE waiting for auth)
      const stored = sessionStorage.getItem(`stack_context_${projectId}`);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          console.log('[AppShell3] Found stack info in sessionStorage:', parsed);
          setStackInfo(parsed);
          return;
        } catch (e) {
          // Invalid JSON, continue to database check
          console.log('[AppShell3] Invalid JSON in sessionStorage');
        }
      }

      // Only do database lookup if we have auth data
      if (!userProfile?.id || !company?.id) {
        return;
      }

      // Fall back to checking the database
      console.log('[AppShell3] Checking database for stack membership...');
      try {
        const { data: settingsData } = await supabase
          .from('user_dashboard_settings')
          .select('stacks')
          .eq('user_id', userProfile.id)
          .eq('company_id', company.id)
          .single();

        console.log('[AppShell3] Database query result:', settingsData);

        if (settingsData?.stacks) {
          // Find if this project is in any stack
          const stackWithProject = settingsData.stacks.find((stack: any) =>
            stack.projectIds?.includes(parseInt(projectId))
          );

          console.log('[AppShell3] Stack with project found:', stackWithProject);

          if (stackWithProject) {
            const info = {
              stackId: stackWithProject.id.toString(),
              stackName: stackWithProject.name
            };
            setStackInfo(info);

            // Store in sessionStorage
            sessionStorage.setItem(`stack_context_${projectId}`, JSON.stringify(info));
          } else {
            // Project is not in any stack, clear any stale sessionStorage
            console.log('[AppShell3] Project not in any stack, clearing sessionStorage');
            sessionStorage.removeItem(`stack_context_${projectId}`);
            setStackInfo(null);
          }
        }
      } catch (error) {
        console.error('[AppShell3] Error checking stack membership:', error);
      }
    };

    checkIfInStack();
  }, [params.projectId, location.state?.fromStack, userProfile?.id, company?.id]);

  const fromStack = stackInfo?.stackId;
  const stackName = stackInfo?.stackName;
  return (
    <motion.div
      className="py-4 md:py-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      key={location.pathname}
    >
      <div className="container mx-auto flex flex-col gap-6 px-4 lg:px-6">
        {/* Back to Stack button - only show if we came from a stack */}
        {fromStack && (
          <div className="flex justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/stack/${fromStack}`)}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <Layers className="h-4 w-4" />
              Back to {stackName || 'Stack'}
            </Button>
          </div>
        )}

        {/* Breadcrumb navigation - only show if breadcrumbs exist */}
        {breadcrumbs.length > 0 && (
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/dashboard">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              {breadcrumbs.map((breadcrumb, index) => (
                <div key={index} className="flex items-center">
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    {breadcrumb.href ? (
                      <BreadcrumbLink asChild>
                        <Link to={breadcrumb.href}>{breadcrumb.label}</Link>
                      </BreadcrumbLink>
                    ) : (
                      <BreadcrumbPage>{breadcrumb.label}</BreadcrumbPage>
                    )}
                  </BreadcrumbItem>
                </div>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        )}

        {/* Main content */}
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">
            {pageTitle}
          </h1>
          {pageDescription && (
            <p className="text-muted-foreground text-sm lg:text-base">
              {pageDescription}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export function AppShell3({ children, breadcrumbs = [], pageTitle = "", pageDescription = "", onSearchClick, onCreateSpace, backgroundColor }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [demoBannerDismissed, setDemoBannerDismissed] = useState(false);
  const [globalToolsOpen, setGlobalToolsOpen] = useState(false);
  const canCreateSpaces = usePermission('spaces.create');
  const { isDemoCompany, getDemoExpiresAt, userProfile } = useAuth();
  const location = useLocation();

  // Determine current tool based on pathname
  const getCurrentTool = useCallback(() => {
    const path = location.pathname;
    if (path.startsWith('/spaces') || path === '/dashboard') return 'spaces';
    if (path.startsWith('/company-team')) return 'users';
    if (path.startsWith('/qualifications')) return 'qualifications';
    if (path.startsWith('/trainers')) return 'trainers';
    if (path.startsWith('/attendance')) return 'attendance';
    if (path.startsWith('/posts')) return 'posts';
    if (path.startsWith('/processes')) return 'processes';
    return null;
  }, [location.pathname]);

  const currentTool = getCurrentTool();

  // Close global tools panel when navigating
  useEffect(() => {
    setGlobalToolsOpen(false);
  }, [location.pathname]);

  // Use provided onSearchClick or internal state
  const handleSearchClick = () => {
    if (onSearchClick) {
      onSearchClick();
    } else {
      setSearchOpen(true);
    }
  };

  const handleGlobalToolsToggle = useCallback(() => {
    setGlobalToolsOpen(prev => !prev);
  }, []);

  // Handle demo expiration
  const handleDemoExpired = () => {
    setShowExpiredModal(true);
  };

  // Handle demo extension request
  const handleRequestExtension = async (email: string) => {
    // For now, just log it - can be enhanced to call an API
    console.log('Extension requested for:', email);
    // Could call an edge function here to handle extension
  };

  // Only render PageHeader4 if there's actual content to show
  const shouldShowHeader = pageTitle !== "" || pageDescription !== "" || breadcrumbs.length > 0;

  // Check if this is a demo company
  const isDemo = isDemoCompany();
  const demoExpiresAt = getDemoExpiresAt();

  return (
    <div className={`min-h-screen flex flex-col ${!backgroundColor ? '' : ''}`} style={backgroundColor ? { backgroundColor } : {}}>
      {/* Demo Banner - shows countdown for demo accounts */}
      {isDemo && !demoBannerDismissed && demoExpiresAt && (
        <DemoBanner
          expiresAt={demoExpiresAt}
          onExpired={handleDemoExpired}
          onDismiss={() => setDemoBannerDismissed(true)}
        />
      )}
      <SharedNavbar
        onCreateSpace={onCreateSpace}
        isGlobalToolsOpen={globalToolsOpen}
        onGlobalToolsToggle={handleGlobalToolsToggle}
      />
      <GlobalToolsExpandingPanel
        isOpen={globalToolsOpen}
        onClose={() => setGlobalToolsOpen(false)}
        currentTool={currentTool}
      />
      {shouldShowHeader && (
        <PageHeader4 breadcrumbs={breadcrumbs} pageTitle={pageTitle} pageDescription={pageDescription} />
      )}
      <div className="w-full flex-1 flex flex-col">
        {children}
      </div>
      {/* QuickActions is now included in every page */}
      {!onSearchClick && (
        <QuickActions
          open={searchOpen}
          setOpen={setSearchOpen}
          onCreateSpace={onCreateSpace}
          canCreateSpaces={canCreateSpaces}
        />
      )}
      {/* Demo Expired Modal */}
      <DemoExpiredModal
        isOpen={showExpiredModal}
        onClose={() => setShowExpiredModal(false)}
        userEmail={userProfile?.email}
        onRequestExtension={handleRequestExtension}
      />
    </div>
  );
}
